import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ROUTES, BASE_PATH } from '@/lib/config';
import { useAuth } from './AuthProvider';
import { useSubscription } from '@/billing/hooks';
import { FullPageSpinner } from '@/components/ui/Spinner';

/**
 * Route guards.
 *
 * While the session is still resolving we render a splash rather than
 * redirecting — otherwise a signed-in customer would flash the login page on
 * every hard refresh before /me returns. These are the client-side convenience
 * gate; the SERVER is the real authority (protected /auth routes 401).
 */

/** Only for signed-in users. Sends guests to login, remembering where they were. */
export function RequireAuth() {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) return <FullPageSpinner />;
  if (!user) {
    // sessionStorage, not router state: signing in reloads the document in a
    // PWA, which discards router state. This is what lets the admin console be
    // its own home-screen shortcut and come back to itself after sign-out.
    try {
      sessionStorage.setItem('smallx_after_login', location.pathname);
    } catch {
      /* private mode - fall back to the default landing screen */
    }
    // Also in the URL. This is the one that actually survives everything: a
    // full document load, a PWA cold start, and any router-state loss between
    // the redirect and the login POST.
    const next = encodeURIComponent(location.pathname);
    return <Navigate to={`${ROUTES.login}?next=${next}`} replace state={{ from: location.pathname }} />;
  }
  return <Outlet />;
}

/**
 * Only for signed-out users (login/signup/forgot). An authed visitor is sent to
 * the trading dashboard at / — which lives OUTSIDE the SPA, so a hard navigation
 * (window.location), not a client route. Doing it here rather than with a
 * <Navigate> makes the post-login target deterministic: the login form and this
 * guard agree on /, with no client-route redirect racing the full navigation.
 */
export function RequireGuest() {
  const { user, isLoading } = useAuth();
  const sub = useSubscription();
  const location = useLocation();
  useEffect(() => {
    if (isLoading || !user) return;
    // Wait for the subscription status under the signed-in session before routing.
    if (sub.isLoading || sub.isFetching) return;
    const s = sub.data;
    // No active plan (and billing is live, and NOT an exempt operator) → the
    // pricing / Billing page so the customer can subscribe.
    const needsPlan = !!s && s.billingConfigured && !s.active && !s.isOperator;
    if (needsPlan) {
      window.location.assign('/app/billing');
      return;
    }

    // Otherwise return to WHERE THEY WERE HEADING. RequireAuth records that in
    // location.state.from; discarding it is why the admin console could not be
    // kept as its own iPhone home-screen shortcut — the shortcut opens
    // /app/admin, sign-out sends it to login, and login used to land everyone
    // on the bot dashboard, so both shortcuts collapsed into the same screen.
    //
    // Only same-origin app paths are honoured, so a crafted state cannot bounce
    // someone off-site after login.
    let stored: string | null = null;
    try {
      stored = sessionStorage.getItem('smallx_after_login');
      if (stored) sessionStorage.removeItem('smallx_after_login');
    } catch {
      /* ignore */
    }
    const qs = new URLSearchParams(window.location.search).get('next');
    const from = qs ?? stored ?? (location.state as { from?: unknown } | null)?.from;

    // react-router pathnames are relative to the /app BASENAME, so what gets
    // recorded is "/admin", not "/app/admin". Validating against "/app/" here
    // rejected the router's own value and fell through to the default every
    // time — which is why both home-screen shortcuts collapsed onto the bot.
    //
    // Validate as a basename-relative path, then prepend the basename for the
    // hard navigation. A leading "//" is refused so a crafted value cannot turn
    // into a protocol-relative URL and bounce someone off-site.
    const ok = typeof from === 'string' && from.startsWith('/') && !from.startsWith('//');
    const rel = ok ? from : ROUTES.dashboard;
    window.location.assign(`${BASE_PATH}${rel}`);
  }, [isLoading, user, sub.isLoading, sub.isFetching, sub.data, location.state]);
  if (isLoading || user) return <FullPageSpinner />;
  return <Outlet />;
}

/**
 * Gate for subscription-only areas (Phase 4A-2 infrastructure). Sends a customer
 * without an active subscription to the billing page. If Stripe isn't configured
 * yet, it does NOT block — the app stays usable pre-launch. In Phase 4B this
 * wraps each customer's own dashboard/bot; the server stays the real authority.
 */
export function RequireSubscription() {
  const sub = useSubscription();
  const location = useLocation();
  if (sub.isLoading) return <FullPageSpinner />;
  const s = sub.data;
  const blocked = s ? s.billingConfigured && !s.active && !s.isOperator : false;
  if (blocked) return <Navigate to={ROUTES.billing} replace state={{ from: location.pathname }} />;
  return <Outlet />;
}
